import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "../../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";

const Slot = ({ n, variant }) => {
    const styles =
        variant === "recycle"
            ? "border-dashed border-nx-blue/50 bg-nx-blue/[0.08] text-nx-blue"
            : "border-nx-lime/30 bg-nx-lime/[0.07] text-nx-lime";
    return (
        <div
            className={`relative flex h-10 w-10 items-center justify-center rounded-xl border text-xs font-extrabold tabular transition-all duration-300 hover:-translate-y-0.5 sm:h-12 sm:w-12 sm:text-sm ${styles}`}
            data-testid={`matrix-slot-${n}`}
        >
            {n}
        </div>
    );
};

const Beam = ({ delay = 0 }) => {
    const reduce = useReducedMotion();
    if (reduce) return null;
    return (
        <motion.span
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 h-px w-24 bg-gradient-to-r from-transparent via-nx-lime/40 to-transparent"
            animate={{ left: ["-12%", "112%"] }}
            transition={{ duration: 7, repeat: Infinity, ease: "linear", delay }}
        />
    );
};

const RULES = [
    { range: "Slots 1–3", name: "First Line", desc: "50% to you directly, 50% spills upward to your own upline." },
    { range: "Slots 4–10", name: "Second Line", desc: "50% to your First Line member, 50% to you directly." },
    { range: "Slots 11–12", name: "Recycle", desc: "50% to your First Line member, 50% recycled upward to your upline." },
];

export const MatrixSystem = () => {
    const { t } = useLanguage();
    return (
        <section id="matrix" data-testid="matrix-section" className="border-t border-white/5 bg-nx-base py-20 sm:py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <SectionHeader
                            label={t("Matrix Income System")}
                            testId="matrix"
                            title={<>{t("A 12-Slot Structure")}<br />{t("with Built-In Recycle.")}</>}
                            description={t("40% of each level price flows into the matrix and fills 12 slots. When slots 11 and 12 are filled, 50% is recycled upward and a new matrix cycle starts automatically — no level is ever frozen.")}
                        />
                        <div className="mt-8 space-y-4">
                            {RULES.map((r, i) => (
                                <Reveal key={r.range} delay={i * 0.08}>
                                    <div className="flex gap-4 rounded-2xl border border-white/5 bg-nx-elev p-5" data-testid={`matrix-rule-${i + 1}`}>
                                        <span className={`mt-0.5 shrink-0 whitespace-nowrap text-[11px] font-extrabold uppercase tracking-[0.14em] ${i === 2 ? "text-nx-blue" : "text-nx-lime"}`}>
                                            {t(r.range)}
                                        </span>
                                        <div>
                                            <h3 className="text-sm font-extrabold text-nx-text">{t(r.name)}</h3>
                                            <p className="mt-1 text-sm leading-relaxed text-nx-sub">{t(r.desc)}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.15} className="h-full">
                            <div className="relative flex h-full flex-col justify-center overflow-hidden rounded-2xl border border-white/5 bg-nx-alt p-6 sm:p-10">
                                <div className="bg-grid-fine absolute inset-0 opacity-60" aria-hidden="true" />
                                <div className="relative">
                                    <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-nx-mute">{t("First Line")}</p>
                                    <div className="relative mt-3 overflow-hidden py-1">
                                        <Beam delay={0} />
                                        <div className="relative flex justify-center gap-2.5 sm:gap-3">
                                            {[1, 2, 3].map((n) => <Slot key={n} n={n} variant="active" />)}
                                        </div>
                                    </div>
                                    <div className="mx-auto my-5 h-px w-2/3 bg-gradient-to-r from-transparent via-white/15 to-transparent" aria-hidden="true" />
                                    <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-nx-mute">{t("Second Line")}</p>
                                    <div className="relative mt-3 overflow-hidden py-1">
                                        <Beam delay={2.4} />
                                        <div className="relative flex flex-wrap justify-center gap-2.5 sm:gap-3">
                                            {[4, 5, 6, 7, 8, 9, 10].map((n) => <Slot key={n} n={n} variant="active" />)}
                                        </div>
                                    </div>
                                    <div className="mx-auto my-5 h-px w-2/3 bg-gradient-to-r from-transparent via-white/15 to-transparent" aria-hidden="true" />
                                    <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-nx-blue/80">{t("Recycle")}</p>
                                    <div className="relative mt-3 overflow-hidden py-1">
                                        <Beam delay={4.8} />
                                        <div className="relative flex justify-center gap-2.5 sm:gap-3">
                                            {[11, 12].map((n) => <Slot key={n} n={n} variant="recycle" />)}
                                        </div>
                                    </div>

                                    <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                                        <span className="inline-flex items-center gap-2 rounded-full border border-nx-lime/30 bg-nx-lime/[0.07] px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-nx-lime" data-testid="matrix-active-slots">
                                            <span className="h-1.5 w-1.5 rounded-full bg-nx-lime" aria-hidden="true" />
                                            {t("10 Active Slots")}
                                        </span>
                                        <span className="inline-flex items-center gap-2 rounded-full border border-nx-blue/40 bg-nx-blue/[0.08] px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-nx-blue" data-testid="matrix-recycle-slots">
                                            <span className="h-1.5 w-1.5 rounded-full bg-nx-blue" aria-hidden="true" />
                                            {t("2 Recycle Slots")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
